import { trackEvent } from '../../analytics/analyticsTracker';
import type { Survey, SurveyPage } from '../../fetchSurvey/types';
import type { SurveyAnswers } from '../../submitSurvey/types';
import type { SurveyPaginationRefs } from '../../surveyPagination/types';
import { buildLogicContext } from '../context/buildLogicContext';
import { resolveEndPageResolution } from '../endPages/resolveEndPageId';
import type { CustomFieldValueMap } from '../types/customFieldValues';
import { simulateActivePath } from '../../surveyProgress/utils/activePath';
import { getAnswerableQuestionIdsOnPage } from '../../surveyProgress/utils/earnedProgress';
import type { LogicAnalyticsEventName, LogicEventHistoryEntry } from './logicAnalyticsTypes';

function summarizeLogicEvent(
  eventName: LogicAnalyticsEventName,
  metadata: Record<string, unknown>
): string {
  switch (eventName) {
    case 'display_logic_matched':
      return `display: ${metadata.questionId} (page ${metadata.pageIndex})`;
    case 'answer_logic_matched':
      return `answer: ${metadata.questionId} ${metadata.entityType} ${metadata.entityId}`;
    case 'question_variant_matched':
      return `variant: ${metadata.questionId} → ${metadata.variantId}`;
    case 'end_page_logic_matched':
      return `end page: ${metadata.endPageId} (${metadata.matchType})`;
    case 'question_viewed':
      return `viewed: ${metadata.questionId}`;
    case 'skip_logic_triggered':
      return `skip: ${metadata.questionId} → ${metadata.targetId}`;
    default:
      return eventName;
  }
}

function incrementLogicCount(refs: SurveyPaginationRefs, eventName: LogicAnalyticsEventName): void {
  switch (eventName) {
    case 'display_logic_matched':
      refs.logicMatchCounts.display += 1;
      break;
    case 'answer_logic_matched':
      refs.logicMatchCounts.answer += 1;
      break;
    case 'question_variant_matched':
      refs.logicMatchCounts.variant += 1;
      break;
    case 'end_page_logic_matched':
      refs.logicMatchCounts.endPage += 1;
      break;
    case 'question_viewed':
      refs.logicMatchCounts.viewed += 1;
      break;
    case 'skip_logic_triggered':
      refs.logicMatchCounts.skip += 1;
      break;
    default:
      break;
  }
}

export function recordLogicEvent(
  refs: SurveyPaginationRefs | null | undefined,
  eventName: LogicAnalyticsEventName,
  metadata: Record<string, unknown>
): void {
  if (!refs) return;

  const timestampMs = typeof metadata.timestampMs === 'number' ? metadata.timestampMs : performance.now();
  const entry: LogicEventHistoryEntry = {
    eventName,
    timestampMs,
    metadata: { ...metadata, timestampMs },
  };

  refs.logicEventHistory.push(entry);
  incrementLogicCount(refs, eventName);
}

function emitLogicEvent(
  refs: SurveyPaginationRefs | null | undefined,
  event: Extract<
    import('../../analytics/types').AnalyticsEvent,
    { eventName: LogicAnalyticsEventName }
  >
): void {
  recordLogicEvent(refs, event.eventName, event.metadata);
  trackEvent(event);
}

export function trackDisplayLogicMatched(
  refs: SurveyPaginationRefs | null | undefined,
  metadata: {
    questionId: string;
    pageIndex: number;
    triggerQuestionId?: string;
  }
): void {
  const timestampMs = performance.now();
  emitLogicEvent(refs, {
    eventName: 'display_logic_matched',
    metadata: {
      ...metadata,
      logicKind: 'display',
      timestampMs,
    },
  });
}

export function trackAnswerLogicMatched(
  refs: SurveyPaginationRefs | null | undefined,
  metadata: {
    questionId: string;
    entityType: 'option' | 'row' | 'column';
    entityId: string;
    pageIndex: number;
    triggerQuestionId?: string;
  }
): void {
  const timestampMs = performance.now();
  emitLogicEvent(refs, {
    eventName: 'answer_logic_matched',
    metadata: {
      ...metadata,
      logicKind: 'answer',
      timestampMs,
    },
  });
}

export function trackQuestionVariantMatched(
  refs: SurveyPaginationRefs | null | undefined,
  metadata: {
    questionId: string;
    variantId: string;
    variantText: string;
    triggerQuestionId?: string;
  }
): void {
  const timestampMs = performance.now();
  emitLogicEvent(refs, {
    eventName: 'question_variant_matched',
    metadata: {
      ...metadata,
      logicKind: 'variant',
      timestampMs,
    },
  });
}

export function trackEndPageLogicMatched(
  refs: SurveyPaginationRefs | null | undefined,
  metadata: {
    endPageId: string;
    matchType: 'conditional' | 'default' | 'fallback';
    previousEndPageId?: string;
    triggerQuestionId?: string;
  }
): void {
  const timestampMs = performance.now();
  emitLogicEvent(refs, {
    eventName: 'end_page_logic_matched',
    metadata: {
      ...metadata,
      logicKind: 'end_page',
      timestampMs,
    },
  });
}

export function trackQuestionViewed(
  refs: SurveyPaginationRefs | null | undefined,
  metadata: {
    questionId: string;
    pageIndex?: number;
  }
): void {
  const timestampMs = performance.now();
  emitLogicEvent(refs, {
    eventName: 'question_viewed',
    metadata: {
      ...metadata,
      timestampMs,
    },
  });
}

export function trackSkipLogicMatched(
  refs: SurveyPaginationRefs | null | undefined,
  metadata: {
    questionId: string;
    skipLogicId: string;
    targetType: 'QUESTION' | 'END_OF_SURVEY';
    targetId: string;
    fromPageIndex: number;
    triggerQuestionId?: string;
  }
): void {
  const timestampMs = performance.now();
  recordLogicEvent(refs, 'skip_logic_triggered', { ...metadata, timestampMs });
  trackEvent({
    eventName: 'skip_logic_triggered',
    metadata: {
      questionId: metadata.questionId,
      skipLogicId: metadata.skipLogicId,
      targetType: metadata.targetType,
      targetId: metadata.targetId,
      fromPageIndex: metadata.fromPageIndex,
    },
  });
}

export function trackLogicSessionSummary(
  refs: SurveyPaginationRefs,
  survey: Survey,
  pages: SurveyPage[],
  allQuestions: Survey['pages'][number]['questions'],
  values: SurveyAnswers,
  customFieldValues: CustomFieldValueMap,
  finalEndPageId: string | undefined
): void {
  const path = simulateActivePath(pages, values, allQuestions, customFieldValues);
  const context = buildLogicContext(values, allQuestions, customFieldValues);
  const pathQuestionIds = new Set<string>();

  for (const pageIndex of path) {
    const page = pages[pageIndex];
    if (!page) continue;
    for (const questionId of getAnswerableQuestionIdsOnPage(page, context)) {
      pathQuestionIds.add(questionId);
    }
  }

  const viewedQuestionIds = refs.logicEventHistory
    .filter(entry => entry.eventName === 'question_viewed')
    .map(entry => String(entry.metadata.questionId ?? ''))
    .filter(Boolean);

  const viewedSet = new Set(viewedQuestionIds);
  const notViewedOnPathCount = [...pathQuestionIds].filter(id => !viewedSet.has(id)).length;

  const displayLogicMatches = refs.logicEventHistory
    .filter(entry => entry.eventName === 'display_logic_matched')
    .map(entry => ({
      questionId: String(entry.metadata.questionId ?? ''),
      pageIndex: Number(entry.metadata.pageIndex ?? 0),
    }));

  const answerLogicMatches = refs.logicEventHistory
    .filter(entry => entry.eventName === 'answer_logic_matched')
    .map(entry => ({
      questionId: String(entry.metadata.questionId ?? ''),
      entityType: entry.metadata.entityType as 'option' | 'row' | 'column',
      entityId: String(entry.metadata.entityId ?? ''),
      pageIndex: Number(entry.metadata.pageIndex ?? 0),
    }));

  const variantMatches = refs.logicEventHistory
    .filter(entry => entry.eventName === 'question_variant_matched')
    .map(entry => ({
      questionId: String(entry.metadata.questionId ?? ''),
      variantId: String(entry.metadata.variantId ?? ''),
    }));

  const skipLogicTriggers = refs.logicEventHistory
    .filter(entry => entry.eventName === 'skip_logic_triggered')
    .map(entry => ({
      questionId: String(entry.metadata.questionId ?? ''),
      skipLogicId: String(entry.metadata.skipLogicId ?? ''),
      targetType: entry.metadata.targetType as 'QUESTION' | 'END_OF_SURVEY',
      targetId: String(entry.metadata.targetId ?? ''),
      fromPageIndex: Number(entry.metadata.fromPageIndex ?? 0),
    }));

  const finalResolution = resolveEndPageResolution(survey, context);

  const logicEventTimeline = refs.logicEventHistory.map(entry => ({
    timestampMs: entry.timestampMs,
    eventName: entry.eventName,
    summary: summarizeLogicEvent(entry.eventName, entry.metadata),
  }));

  trackEvent({
    eventName: 'logic_session_summary',
    metadata: {
      displayLogicMatches,
      answerLogicMatches,
      variantMatches,
      endPageResolution: {
        finalEndPageId,
        matchType: finalEndPageId ? finalResolution.matchType : 'fallback',
        changeCount: refs.endPageChangeCount,
      },
      skipLogicTriggers,
      viewedQuestionIds,
      viewedCount: viewedSet.size,
      notViewedOnPathCount,
      logicEventTimeline,
    },
  });
}
